/**
 * Générateur — Automatisme : trouver le nombre d'éléments pour une étape
 * donnée dans une suite de motifs évolutive.
 * Attendu ID : 1061 | Niveau : 5ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8, ligne 601).
 * Cadrage du sous-agent programme-maths, arbitré par le prof à l'épreuve de
 * Musk les 2026-09-09 et 2026-09-10.
 *
 * Trois attendus, trois gestes, et c'est la NATURE DE LA RÉPONSE qui trie :
 *   1060  l'étape SUIVANTE, par itération ;
 *   1061  un NOMBRE, à un rang donné : c'est ici ;
 *   1062  un CALCUL non réduit qui décrit la fabrication.
 * La stratégie est libre, c'est le RANG qui pilote la difficulté. RANGS 5 À 8
 * SEULEMENT (arbitrage prof du 2026-09-10) : l'ITÉRATION suffit, deux à cinq
 * pas au-delà des trois valeurs écrites. Dès que le rang s'éloigne sans que la
 * règle soit donnée, on empile 1062 sur 1061 et un échec ne dit plus lequel a
 * lâché ; et une question qui oblige à découvrir une méthode n'est plus un
 * automatisme mais une phase de recherche. Le rang lointain vit donc en 1062,
 * où le calcul s'écrit sans jamais se résoudre. La troisième voie possible,
 * « règle fournie dans l'énoncé, rang libre », est écartée : c'est gen_638.
 *
 * Plan de cartes validé, deux cartes :
 *   - Série flash        (une figure, les étapes 1 à 3 ÉCRITES puis deux rangs
 *                         de 5 à 8 à trouver, en tableau aligné)
 *   - Combien à l'étape ? (QCM « Combien de carreaux à l'étape 7 ? » 3 choix
 *                         par rang de la clé ; Vrai/Faux 3 phrases dont la
 *                         proportionnalité abusive)
 * Supprimés à l'épreuve de Musk : l'étape suivante (1060), le calcul structuré
 * « 1 + 15 × 4 » (1062), la règle donnée dans l'énoncé et les suites de
 * nombres pures (638), la lettre (932), tout rang au-delà de 8 (il exigerait
 * la structure, donc 1062), le
 * sens inverse (équation, 4e), la saisie nue (doublon du QCM), les figures à
 * ajouts croissants (à un rang lointain, leur formule est n², Puissances).
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * Banque commune (./motifs.js), figures à ajout constant seulement :
 * structurées (croix, L, cadre, tours, allumettes en carrés et en triangles)
 * et rangées. Rang de 5 à 8, valeur ≤ 100 pour rester mental.
 *
 * SACRIFIÉ pour le coup de pouce : le couple (fixe 1, groupe 4), la suite
 * 5 ; 9 ; 13 du carrelage de 638 (même famille de fiches que 1060) ; il exclut
 * la croix et les rangées (1, 4).
 *
 * ── Distracteurs (revue de 638 du 2026-08-01, cadrage prof 2026-09-10) ────
 *   proportionnel   : N × groupe, il oublie le départ — L'ERREUR PRINCIPALE ;
 *   ajout_de_trop   : la valeur de l'étape N + 1 ;
 *   ajout_manquant  : la valeur de l'étape N − 1 ;
 *   doublement      : 2 × étape N/2, la proportionnalité abusive ;
 *   pas_mal_lu      : le groupe lu à ± 1 (sur les allumettes, le côté partagé
 *                     compté deux fois : 4 par carré au lieu de 3).
 * Chaque distracteur est vérifié différent de la clé ; les deux distracteurs
 * du QCM sont tirés par RANG de la clé (elle n'est ni toujours au milieu, ni
 * toujours extrême) ; résidu mesuré à la revue.
 */
import { randomChoice, shuffle, makeQcm, makeVraiFaux, makeTexteTrou } from '../../../utils';
import { tirerLineaire, elImage, paramsMotif, sansRepetition, deNom, pluriel } from './motifs';

const ATTENDU_ID = 1061;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_COMBIEN = 'Combien à l\'étape ?';

const SACRIFIE = { fixe: 1, groupe: 4 };
const VALEUR_MAX = 100;
/** Cf. l'en-tête : rangs 5 à 8, l'itération suffit, 1062 n'est jamais empilé. */
const RANG_MIN = 5;
const RANG_MAX = 8;
/** Les étapes dont la valeur est ÉCRITE en tête de la Série flash. */
const DONNEES = [1, 2, 3];
const tirer = () => tirerLineaire({ sacrifie: SACRIFIE });

/** Les rangs dont la valeur reste mentale, dans la plage atteignable. */
const rangs = (m, min, max) => {
    const out = [];
    for (let N = min; N <= max; N++) if (m.valeur(N) <= VALEUR_MAX) out.push(N);
    return out;
};
/** Le rang du QCM : la plage atteignable, 5 à 8. */
const rangQcm = (m) => { const d = rangs(m, RANG_MIN, RANG_MAX); return d.length ? randomChoice(d) : null; };

/**
 * Rappel ÉCRAN SEULEMENT (convention du parc : 613, 615, 619, 1044, 1045 ;
 * PrintEnonce retire tout paragraphe `italic text-slate-400`, l'élève ayant sa
 * feuille sur papier). Formulé « de tête, ou au brouillon » : c'est un
 * automatisme, on vise le calcul mental et le brouillon est un filet, pas une
 * consigne (demande prof 2026-09-10). Seulement ici : en 1060 on choisit une
 * figure sans calculer, en 1062 on écrit un calcul sans le résoudre.
 */
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Continue de tête, ou note les étapes au brouillon.</p>';

const EXPLICATIONS = (m) => ({
    regle: `Au départ il y a ${pluriel(m.fixe, m.nom)}, puis ${m.groupe} par étape : à l'étape N, ${m.fixe} + N × ${m.groupe}.`,
    proportionnel: `${pluriel(m.fixe, m.nom)} au départ : on ne part pas de 0.`,
    ajout_de_trop: 'C\'est le nombre de l\'étape d\'après.',
    ajout_manquant: 'C\'est le nombre de l\'étape d\'avant.',
    doublement: `L'étape 10 n'est pas deux fois l'étape 5 : le départ (${m.fixe}) ne compte qu'une fois.`,
    pas_mal_lu: `Compte ce qui s'ajoute d'une étape à l'autre : ${pluriel(m.groupe, m.nom)}.`,
});

// ─── Carte 1 : Série flash (les étapes 1 à 3 écrites, deux rangs à trouver) ───

const genFlash = () => {
    for (let essai = 0; essai < 60; essai++) {
        const m = tirer();
        // DEUX rangs seulement : la plage 5 à 8 n'en offre que quatre, trois
        // trous l'épuisaient à chaque tirage et la variété s'effondrait
        const dispo = rangs(m, RANG_MIN, RANG_MAX);
        if (dispo.length < 2) continue;
        const choisis = shuffle(dispo).slice(0, 2).sort((a, b) => a - b);
        // Les trois premières valeurs sont ÉCRITES (demande prof 2026-09-10) :
        // 1061 demande de produire un nombre, pas de recompter le dessin. La
        // série devient le tableau étape par nombre que le programme de CM2
        // associe à ces motifs, et l'élève peut itérer ou grouper ses sauts.
        // Jamais en 1062 : il y trouverait la structure par ajustement
        // numérique, sans regarder la figure.
        const phrase = [
            ...DONNEES.map((n) => `Étape ${n} : ${m.valeur(n)}`),
            ...choisis.map((N) => `Étape ${N} : {{${m.valeur(N)}}}`),
        ].join('\n');
        const texte = `${RAPPEL_BROUILLON}<p><b>Complète.</b></p>{{element:0}}`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_FLASH), mode: 'saisie', strict: true, elements_additionnels: elImage(m) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
        payload._meta.params = paramsMotif(m, { etapes: choisis, donnees: DONNEES });
        return payload;
    }
    // Repli déterministe : L de points (3 ; 5 ; 7), étapes 6 et 8.
    const m = tirerLineaire({ sacrifie: SACRIFIE, structureesSeules: true });
    const choisis = [6, 8].filter((N) => m.valeur(N) <= VALEUR_MAX);
    const phrase = [
        ...DONNEES.map((n) => `Étape ${n} : ${m.valeur(n)}`),
        ...choisis.map((N) => `Étape ${N} : {{${m.valeur(N)}}}`),
    ].join('\n');
    const payload = makeTexteTrou(phrase, `${RAPPEL_BROUILLON}<p><b>Complète.</b></p>{{element:0}}`,
        { ...opts(L_FLASH), mode: 'saisie', strict: true, elements_additionnels: elImage(m) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
    payload._meta.params = paramsMotif(m, { etapes: choisis, donnees: DONNEES });
    return payload;
};

// ─── Carte 2, format A : QCM du nombre ───────────────────────────────────

const tousCandidats = (m, N) => {
    const cle = m.valeur(N);
    const c = [];
    const pousser = (code, val) => {
        if (Number.isInteger(val) && val > 0 && val !== cle && !c.some((x) => x.val === val)) c.push({ code, val });
    };
    pousser('proportionnel', N * m.groupe);
    pousser('ajout_de_trop', m.valeur(N + 1));
    pousser('ajout_manquant', m.valeur(N - 1));
    if (N % 2 === 0) pousser('doublement', 2 * m.valeur(N / 2));
    pousser('pas_mal_lu', m.fixe + N * (m.groupe + 1));
    return c;
};

/** Deux distracteurs, la paire tirée par RANG de la clé (0, 1 ou 2 en dessous). */
const distracteursQcm = (m, N) => {
    const cands = tousCandidats(m, N);
    const cle = m.valeur(N);
    const parRang = new Map();
    for (let i = 0; i < cands.length; i++) {
        for (let j = i + 1; j < cands.length; j++) {
            const t = [cands[i], cands[j]];
            const rang = t.filter((x) => x.val < cle).length;
            if (!parRang.has(rang)) parRang.set(rang, []);
            parRang.get(rang).push(t);
        }
    }
    if (!parRang.size) return null;
    return randomChoice(parRang.get(randomChoice([...parRang.keys()])));
};

const genQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const m = tirer();
        const N = rangQcm(m);
        if (!N) continue;
        const faux = distracteursQcm(m, N);
        if (!faux) continue;
        const bon = String(m.valeur(N));
        const texte = `${RAPPEL_BROUILLON}{{element:0}}<p><b>Combien ${deNom(m.nom)} à l'étape ${N} ?</b></p>`;
        const payload = makeQcm([bon, ...faux.map((x) => String(x.val))], bon, texte, { ...opts(L_COMBIEN), elements_additionnels: elImage(m) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
        payload._meta.params = paramsMotif(m, { N, distracteurs: faux.map(({ code }) => ({ code })) });
        return payload;
    }
    // Repli déterministe : L de points (3 ; 5 ; 7), étape 12 → 25 ; 24 (proportionnel), 27 (ajout_de_trop).
    const m = tirerLineaire({ sacrifie: SACRIFIE, structureesSeules: true });
    const N = 12;
    const payload = makeQcm(
        [String(m.valeur(N)), String(N * m.groupe), String(m.valeur(N + 1))].filter((v, i, t) => t.indexOf(v) === i),
        String(m.valeur(N)), `${RAPPEL_BROUILLON}{{element:0}}<p><b>Combien ${deNom(m.nom)} à l'étape ${N} ?</b></p>`,
        { ...opts(L_COMBIEN), elements_additionnels: elImage(m) },
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
    payload._meta.params = paramsMotif(m, { N, distracteurs: [{ code: 'proportionnel' }, { code: 'ajout_de_trop' }] });
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux ─────────────────────────────────────

/** « L'étape 12 a 25 carreaux. » */
const ligneNombre = (m, N, vraie) => {
    const v = m.valeur(N);
    if (vraie) return { ligne: `L'étape ${N} a ${pluriel(v, m.nom)}.`, vraie: true, code: null };
    const d = randomChoice(tousCandidats(m, N));
    return { ligne: `L'étape ${N} a ${pluriel(d.val, m.nom)}.`, vraie: false, code: d.code };
};

/** « L'étape 12 a deux fois plus de carreaux que l'étape 6. » : vraie seulement si le départ est nul, donc jamais ici. */
const ligneDouble = (m, N) => {
    const moitie = N / 2;
    const vraie = m.valeur(N) === 2 * m.valeur(moitie);
    return {
        ligne: `L'étape ${N} a deux fois plus ${deNom(m.nom)} que l'étape ${moitie}.`,
        vraie, code: vraie ? null : 'doublement',
    };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const m = tirer();
        const pairs = rangs(m, RANG_MIN, RANG_MAX).filter((N) => N % 2 === 0 && m.valeur(N / 2) > 0);
        const dispo = rangs(m, RANG_MIN, RANG_MAX);
        if (!pairs.length || dispo.length < 3) continue;
        const Ndouble = randomChoice(pairs);
        const [N1, N2] = shuffle(dispo.filter((N) => N !== Ndouble)).slice(0, 2);
        // les deux lignes « nombre » : une vraie, une fausse
        const verites = shuffle([true, false]);
        const affs = shuffle([ligneDouble(m, Ndouble), ligneNombre(m, N1, verites[0]), ligneNombre(m, N2, verites[1])]);
        const nbVraies = affs.filter((a) => a.vraie).length;
        if (nbVraies < 1 || nbVraies > 2) continue;
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const texte = `${RAPPEL_BROUILLON}<p><b>Vrai ou faux ?</b></p>{{element:0}}`;
        const payload = makeVraiFaux(lignes, affs.map((a) => (a.vraie ? 0 : 1)), texte, { ...opts(L_COMBIEN), elements_additionnels: elImage(m) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
        payload._meta.params = paramsMotif(m, { verites: affs.map((a) => a.vraie), distracteurs: affs.filter((a) => a.code).map(({ code }) => ({ code })) });
        return payload;
    }
    // Repli déterministe : L de points : « l'étape 10 a 21 » vrai ; « l'étape 12 a 27 » faux ; « deux fois plus » faux.
    const m = tirerLineaire({ sacrifie: SACRIFIE, structureesSeules: true });
    const payload = makeVraiFaux(
        [`L'étape 10 a ${pluriel(m.valeur(10), m.nom)}.`,
            `L'étape 12 a ${pluriel(m.valeur(13), m.nom)}.`,
            `L'étape 12 a deux fois plus ${deNom(m.nom)} que l'étape 6.`],
        [0, 1, 1], `${RAPPEL_BROUILLON}<p><b>Vrai ou faux ?</b></p>{{element:0}}`, { ...opts(L_COMBIEN), elements_additionnels: elImage(m) },
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
    payload._meta.params = paramsMotif(m, { verites: [true, false, false], distracteurs: [{ code: 'ajout_de_trop' }, { code: 'doublement' }] });
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'banque commune (./motifs.js), figures à ajout constant seulement (les croissantes sont en 1060 : à un rang lointain leur formule est n²) ; rien d\'écrit sur la figure, la règle est à lire (contre 638 où elle est donnée)',
        'rang de 5 à 20, valeur ≤ 100 : le rang 100 est hors calcul mental, il vit en 1062 où la valeur n\'est jamais calculée',
        'le couple (fixe 1, groupe 4), suite 5 ; 9 ; 13, SACRIFIÉ pour la fiche : il exclut la croix et les rangées (1, 4)',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    combien_qcm_unique: '« Combien de carreaux à l\'étape 15 ? », rang de 6 à 20 (lointain deux fois sur trois : la stratégie est libre, c\'est le rang qui pilote la difficulté), trois choix, deux familles distinctes parmi proportionnel (N × groupe, la principale), ajout_de_trop (étape N + 1), ajout_manquant (étape N − 1), doublement (2 × étape N/2), pas_mal_lu (groupe + 1) ; la paire est tirée par rang de la clé, mesuré à la revue',
    combien_tableau_vf: 'trois phrases, 1 ou 2 vraies : DEUX lignes « l\'étape N a v » (l\'une vraie, l\'autre fausse : les cinq familles) et une ligne « l\'étape N a deux fois plus que l\'étape N/2 », la proportionnalité abusive, fausse tant que le départ n\'est pas nul',
    flash_texte_trou_saisie: 'une figure, quatre rangs : deux proches (5 à 8, l\'itération suffit) et deux lointains (10 à 20, elle devient coûteuse) ; c\'est le rang qui pilote la difficulté',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    combien_qcm_unique: genQcm,
    combien_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'combien', label: L_COMBIEN, formatsList: [
        { key: 'combien_qcm_unique', label: 'QCM' },
        { key: 'combien_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    // mémoire courte : jamais deux fois la même variante dans une série (cf. sansRepetition)
    const payload = sansRepetition(key, GENERATORS[key]);
    payload._meta.generatorKey = key;
    return payload;
};
